import { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import AuthCard from "@/components/auth/AuthCard";
import AuthInput from "@/components/auth/AuthInput";
import AuthButton from "@/components/auth/AuthButton";
import AuthLink from "@/components/auth/AuthLink";
import { useAuth, formatAuthError } from "@/context/AuthContext";

export default function Login() {
  const { login, magicLink, resendVerification } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [sendingMagic, setSendingMagic] = useState(false);
  const [unverifiedEmail, setUnverifiedEmail] = useState("");
  const [resendingVerify, setResendingVerify] = useState(false);

  const from = location.state?.from || "/collections";
  const resetOk = new URLSearchParams(location.search).get("reset") === "success";

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const authedUser = await login(email.trim().toLowerCase(), password);
      // Defensive guard: the AuthContext login call now throws on a
      // user-less response, so this `if` is a belt-and-braces check
      // ensuring we never fall through to the redirect without a
      // confirmed authenticated user state.
      if (!authedUser) {
        setError("Login failed. Please try again.");
        return;
      }
      // iOS Safari zooms into an input on focus when its font-size < 16px
      // and does not automatically reset the visual viewport on route
      // change. Manual sign-in leaves the last focused password field
      // holding that zoomed state, which is why the Dashboard loads
      // zoomed + horizontally scrollable only in the typed-credentials
      // flow (saved-password autofill never receives focus and so the
      // browser stays at 1.0). Blur the active element, reassert the
      // canonical viewport meta, and clear any transform/width the auth
      // card CSS may have left on #root / <body> BEFORE the redirect
      // hands the page over to the Dashboard container.
      try {
        if (typeof document !== "undefined") {
          if (document.activeElement && typeof document.activeElement.blur === "function") {
            document.activeElement.blur();
          }
          // Visual-viewport hard reset (mobile Safari). The layout viewport
          // was already normalised in Fix 1D, but Safari maintains a
          // separate visual viewport that persists at the pre-navigate
          // zoom/offset after input focus. Scroll origin + a transient
          // `zoom: 1.0` on <html> forces Safari to snap the visual
          // viewport back to (0,0) at scale 1.0, then the max-scale meta
          // toggle re-locks initial-scale=1.0 for the Dashboard render.
          try { window.scrollTo(0, 0); } catch { /* noop */ }
          try {
            document.documentElement.style.zoom = "1.0";
            setTimeout(() => {
              try { document.documentElement.style.zoom = ""; } catch { /* noop */ }
            }, 50);
          } catch { /* noop */ }
          const meta = document.querySelector('meta[name="viewport"]');
          if (meta) {
            meta.setAttribute(
              "content",
              "width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no, viewport-fit=cover"
            );
            // Force a re-read on iOS Safari by momentarily toggling the
            // maximum-scale flag. Without this, the visual viewport
            // stays pinned at the pre-navigate zoom level.
            setTimeout(() => {
              meta.setAttribute(
                "content",
                "width=device-width, initial-scale=1.0, viewport-fit=cover"
              );
            }, 50);
          }
          for (const el of [document.documentElement, document.body, document.getElementById("root")]) {
            if (!el || !el.style) continue;
            el.style.width = "100%";
            el.style.maxWidth = "100%";
            el.style.overflowX = "hidden";
            el.style.transform = "none";
          }
        }
      } catch { /* noop */ }
      sessionStorage.clear();
      localStorage.removeItem('lastRoute');
      navigate("/home", { replace: true });
    } catch (err) {
      // Detect the "email not verified" gate returned by the backend
      // login route (403 with structured detail) so we can offer the
      // resend-verification affordance below the password error.
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 403 && typeof detail === "object" && detail?.code === "email_not_verified") {
        setError(detail.message || "Please verify your email before logging in.");
        setUnverifiedEmail(detail.email || email.trim().toLowerCase());
      } else {
        setError(formatAuthError(err, "Invalid email or password."));
        setUnverifiedEmail("");
      }
    } finally { setBusy(false); }
  };

  const onResendVerification = async () => {
    if (!unverifiedEmail) return;
    setResendingVerify(true);
    try {
      await resendVerification(unverifiedEmail);
      toast.success("Verification email sent.");
    } catch (err) {
      toast.error(formatAuthError(err, "Could not resend verification email."));
    } finally { setResendingVerify(false); }
  };

  const onMagic = async () => {
    if (!email.trim()) { setError("Enter your email to receive a magic link."); return; }
    setSendingMagic(true);
    setError("");
    try {
      await magicLink(email.trim().toLowerCase());
      navigate("/auth/magic-link-sent", { state: { email: email.trim().toLowerCase() } });
    } catch (err) {
      setError(formatAuthError(err));
    } finally { setSendingMagic(false); }
  };

  return (
    <AuthCard
      title="Welcome back to CDI Collectables"
      subtitle="Sign in to your account"
      footer={<AuthLink to="/auth/signup" testid="login-link-signup">Start your 3-day free trial</AuthLink>}
    >
      {resetOk && (
        <div className="mb-4 p-3 rounded text-xs" data-testid="login-reset-banner"
             style={{ background: "#1f2f1f", border: "1px solid #3a5a3a", color: "#a8dca8" }}>
          Password reset. Sign in with your new password.
        </div>
      )}
      <form onSubmit={onSubmit}>
        <AuthInput label="Email" type="email" required
          testid="login-input-email" value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@example.com" autoComplete="email" />
        <div className="relative">
          <AuthInput label="Password" type={showPassword ? "text" : "password"} required
            testid="login-input-password" value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="••••••••" autoComplete="current-password"
            error={error && !sendingMagic ? error : ""} />
          <button
            type="button"
            onClick={() => setShowPassword((v) => !v)}
            aria-label={showPassword ? "Hide password" : "Show password"}
            data-testid="login-toggle-password"
            className="absolute right-2 flex items-center justify-center"
            style={{
              top: "26px",
              width: "32px",
              height: "40px",
              background: "transparent",
              border: "none",
              color: "#c9c9c9",
              cursor: "pointer",
              padding: 0,
            }}
          >
            {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
          </button>
        </div>
        <div className="text-xs vp-muted mt-1">Minimum 8 characters</div>
        <div className="mb-4 text-right">
          <AuthLink to="/auth/reset-request" testid="login-link-forgot">Forgot password?</AuthLink>
        </div>
        {/* Terms & Privacy acknowledgement — display-only on login. */}
        <p
          className="text-xs mb-3"
          style={{ color: "#9a9a9a", lineHeight: 1.5 }}
          data-testid="login-legal-acknowledgement"
        >
          By continuing, you agree to our{" "}
          <Link to="/legal/terms" data-testid="login-legal-terms-link" style={{ color: "#C9A86A", textDecoration: "underline" }}>
            Terms of Use
          </Link>{" "}
          and{" "}
          <Link to="/legal/privacy" data-testid="login-legal-privacy-link" style={{ color: "#C9A86A", textDecoration: "underline" }}>
            Privacy Policy
          </Link>
          .
        </p>
        <AuthButton type="submit" testid="login-submit" loading={busy}>Sign in</AuthButton>
        {unverifiedEmail && (
          <div className="mt-3 text-center" data-testid="login-verify-prompt">
            <button
              type="button"
              onClick={onResendVerification}
              disabled={resendingVerify}
              data-testid="login-resend-verification"
              style={{
                background: "transparent",
                border: "none",
                color: "#c9a86a",
                textDecoration: "underline",
                cursor: resendingVerify ? "wait" : "pointer",
                fontSize: "0.75rem",
                padding: 0,
              }}
            >
              {resendingVerify ? "Sending…" : "Resend verification email"}
            </button>
          </div>
        )}
      </form>
      <div className="mt-3">
        <AuthButton type="button" variant="charcoal" onClick={onMagic}
                    testid="login-magic-link" loading={sendingMagic}>
          Email me a sign-in link
        </AuthButton>
      </div>
      {/* Suppress unused import lint */}
      <span data-testid="login-toast-hook" style={{ display: "none" }} onClick={() => toast("noop")} />
    </AuthCard>
  );
}
